/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

const MDN_HTTP_REFERENCE =
	'Please refer to <https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods> for more information about the different HTTP methods';

const HTTP_URL_PARAMETER = {
	type: 'String',
	name: 'URL',
	description: 'The URL to request.',
	optional: false
};

const HTTP_BODY_PARAMETER = {
	type: 'String',
	name: 'Body',
	description: 'The body of the request.',
	optional: true
};

const HTTP_CONFIG_PARAMETER = {
	type: 'JSON',
	name: 'ConfigJSON',
	description: 'A JSON with one or more of the following properties:<br />' +
		'<ul>' +
			'<li><b>baseURL</b>: string</li>' +
			'<li><b>timeout</b>: positive number, timout in milliseconds</li>' +
			'<li>' +
				'<b>auth</b>: object with the following properties' +
				'<ul>' +
					'<li><b>username</b>: string</li>' +
					'<li><b>password</b>: string</li>' +
				'</ul>' +
			'</li>' +
			'<li>' +
				'<b>proxy</b>: object one or more of the following properties' +
			'<ul>' +
				'<li><b>protocol</b>: string, "http" or "https"</li>' +
				'<li><b>host</b>: string, hostname or IP of the proxy server</li>' +
				'<li><b>port</b>: number</li>' +
				'<li>' +
					'<b>auth</b>: object with the following properties' +
					'<ul>' +
						'<li><b>username</b>: string</li>' +
						'<li><b>password</b>: string</li>' +
					'</ul>' +
				'</li>' +
			'</ul>' +
			'</li>' +
		'</ul>',
	optional: true
};

const HTTP_HEADER_PARAMETER = {
	type: 'JSON',
	name: 'HeadersJSON',
	description: 'Headers of the HTTP request.',
	optional: true
};
const TARGET_PARAMETER = {
	type: 'Target',
	name: 'Target',
	description:
		'INBOX(), OUTBOX("MsgID") or a cell range where the result will be placed. Make sure the cell range is big enough or not everything is displayed.',
	optional: true
};

module.exports = {
	types: {
		// Target: [{
		// 	value: 'INBOX',
		// }, {
		// 	value: 'OUTBOX("Message")',
		// }],
		// method as supported by axios:
		HttpMethod: [
			{
				value: 'GET',
				description: 'Request a specified resource.'
			},
			{
				value: 'DELETE',
				description: 'Delete a specified resource.'
			},
			{
				value: 'HEAD',
				description: 'Analog to GET, but without the response body.'
			},
			{
				value: 'OPTIONS',
				description: 'Use to describe communication options for a specified target resource.'
			},
			{
				value: 'POST',
				description: 'Submit an entity to a specified resource.'
			},
			{
				value: 'PUT',
				description: 'Replace specified target resource with the request payload.'
			},
			{
				value: 'PATCH',
				description: 'Apply partial modifications to a specified resource.'
			}
		]
	},
	functions: {
		'HTTP.DELETE': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP DELETE request. Commonly used to delete a resource on the server. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP DELETE request',
				arguments: [HTTP_URL_PARAMETER, HTTP_HEADER_PARAMETER, HTTP_CONFIG_PARAMETER, TARGET_PARAMETER],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.DELETE() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.DELETE("https://www.example.org/user/0)',
							result: 'Sends a DELETE Request to the API'
						},
						{
							formula: '=HTTP.DELETE("https://www.example.org/user/0, JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a DELETE Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.DELETE("https://www.example.org/user/0, , , INBOX("Sheet1"))',
							result: 'Sends a DELETE Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'HTTP.GET': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP GET request. Used to retrieve data from a server. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP GET request',
				arguments: [HTTP_URL_PARAMETER, HTTP_HEADER_PARAMETER, HTTP_CONFIG_PARAMETER, TARGET_PARAMETER],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.GET() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.GET("https://www.example.org/user/0)',
							result: 'Sends a GET Request to the API'
						},
						{
							formula: '=HTTP.GET("https://www.example.org/user/0, JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a GET Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.GET("https://www.example.org/user/0, , , INBOX("Sheet1"))',
							result: 'Sends a GET Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'HTTP.HEAD': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP HEAD request. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP HEAD request',
				arguments: [HTTP_URL_PARAMETER, HTTP_HEADER_PARAMETER, HTTP_CONFIG_PARAMETER, TARGET_PARAMETER],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.HEAD() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.HEAD("https://www.example.org/user/0)',
							result: 'Sends a HEAD Request to the API'
						},
						{
							formula: '=HTTP.HEAD("https://www.example.org/user/0, JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a HEAD Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.HEAD("https://www.example.org/user/0, , , INBOX("Sheet1"))',
							result: 'Sends a HEAD Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'HTTP.OPTIONS': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP OPTIONS request. The HTTP OPTIONS method is used to describe communication options for the target resource. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP OPTIONS request',
				arguments: [HTTP_URL_PARAMETER, HTTP_HEADER_PARAMETER, HTTP_CONFIG_PARAMETER, TARGET_PARAMETER],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.OPTIONS() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.OPTIONS("https://www.example.org/user/0)',
							result: 'Sends a OPTIONS Request to the API'
						},
						{
							formula: '=HTTP.OPTIONS("https://www.example.org/user/0, JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a OPTIONS Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.OPTIONS("https://www.example.org/user/0, , , INBOX("Sheet1"))',
							result: 'Sends a OPTIONS Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'HTTP.PATCH': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP PATCH request. The HTTP PATCH method is used to partially modify the specified resource on the server. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP PATCH request',
				arguments: [
					HTTP_URL_PARAMETER,
					HTTP_BODY_PARAMETER,
					HTTP_HEADER_PARAMETER,
					HTTP_CONFIG_PARAMETER,
					TARGET_PARAMETER
				],
				return: {
					type: 'Number',
					description:
						'The function HTTP.PATCH() always returns a unique random request ID. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: []
				}
			}
		},
		'HTTP.POST': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP POST request. Used to create a resource on the server or to generally send data to the server. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP POST request',
				arguments: [
					HTTP_URL_PARAMETER,
					{
						type: 'String',
						name: 'Body',
						description: 'The body of the request',
						optional: true
					},
					HTTP_HEADER_PARAMETER,
					HTTP_CONFIG_PARAMETER,
					TARGET_PARAMETER
				],
				return: {
					type: '',
					description:
						'The function HTTP.POST() always returns a unique random request ID. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: []
				}
			}
		},
		'HTTP.PUT': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP PUT request. Used to replace an existing resource on the server. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP PUT request',
				arguments: [
					HTTP_URL_PARAMETER,
					HTTP_BODY_PARAMETER,
					HTTP_HEADER_PARAMETER,
					HTTP_CONFIG_PARAMETER,
					TARGET_PARAMETER
				],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.PUT() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'|5|||\n' +
						'|6|id|5 |\n' +
						'|7|data|exampleData |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.PUT("https://www.example.org/user/0, JSON(A6:B7))',
							result: 'Sends a PUT Request to the API'
						},
						{
							formula:
								'=HTTP.PUT("https://www.example.org/user/0, JSON(A6:B7), JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a PUT Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.PUT("https://www.example.org/user/0, JSON(A6:B7), , , INBOX("Sheet1"))',
							result: 'Sends a PUT Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'HTTP.REQUEST': {
			default: {
				category: 'HTTP',
				description: `Create an HTTP request. Universal function to use for all HTTP methods. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Sends an HTTP request',
				arguments: [
					HTTP_URL_PARAMETER,
					{
						type: 'HttpMethod',
						name: 'Method',
						description: 'The HTTP-method of the request.',
						optional: false
					},
					HTTP_BODY_PARAMETER,
					HTTP_HEADER_PARAMETER,
					HTTP_CONFIG_PARAMETER,
					TARGET_PARAMETER
				],
				return: {
					type: 'Number',
					description:
						'The function HTTP.OPTIONS() always returns a unique random request ID. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: []
				}
			}
		},
		'HTTP.TRACE': {
			default: {
				category: 'HTTP',
				description: `Send an HTTP TRACE request. Used to debug web server connections by returning the full HTTP request to the client for proxy-debugging purposes. ${MDN_HTTP_REFERENCE}`,
				inlineDescription: 'Create an HTTP TRACE request.',
				arguments: [HTTP_URL_PARAMETER, HTTP_HEADER_PARAMETER, HTTP_CONFIG_PARAMETER, TARGET_PARAMETER],
				return: {
					type: 'HTTP Response or Error',
					description:
						'The function HTTP.TRACE() returns a HTTP response JSON. Otherwise an [error]($HelpLink$/other/errorcodes) is displayed.'
				},
				examples: {
					infoStart:
						'\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Headers| |\n' +
						'|2|Accept|application/json |\n' +
						'|3|Config| |\n' +
						'|4|timeout|3000 |\n' +
						'\n\nExamples for using the config and header parameter. Use a JSON() function around the cell ranges.',
					infoEnd: '',
					formulas: [
						{
							formula: '=HTTP.TRACE("https://www.example.org/user/0)',
							result: 'Sends a TRACE Request to the API'
						},
						{
							formula: '=HTTP.TRACE("https://www.example.org/user/0, JSON(A2:B2), JSON(A4:B4))',
							result: 'Sends a TRACE Request to the API',
							comment:
								'Use the "Accept: application/json" header and a timeout of 3 seconds for the request.'
						},
						{
							formula: '=HTTP.TRACE("https://www.example.org/user/0, , , INBOX("Sheet1"))',
							result: 'Sends a TRACE Request to the API',
							comment: 'The HTTP response is placed into the inbox of "Sheet1"'
						}
					]
				}
			}
		},
		'URL.HASH': {
			default: {
				category: 'HTTP',
				description: 'Gets the fragment of a URL.',
				inlineDescription: 'Gets the fragment of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'The hash'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.HASH("http://cedalo.com#a_value")',
							result: '#a_value'
						}
					]
				}
			}
		},
		'URL.HOST': {
			default: {
				category: 'HTTP',
				description: 'Gets the host of a URL.',
				inlineDescription: 'Gets the host of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Host of URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.HOST("mqtt://test.mosquitto.org:1883")',
							result: 'test.mosquitto.org:1883'
						}
					]
				}
			}
		},
		'URL.HOSTNAME': {
			default: {
				category: 'HTTP',
				description: 'Gets the hostname of a URL.',
				inlineDescription: 'Gets the hostname of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Hostname of URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.HOSTNAME("https://cedalo.com/blog/")',
							result: 'cedalo.com'
						}
					]
				}
			}
		},
		'URL.ORIGIN': {
			default: {
				category: 'HTTP',
				description: 'Gets the origin of a URL.',
				inlineDescription: 'Gets the origin of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Origin of a URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.ORIGIN("https://cedalo.com/blog/")',
							result: 'https://cedalo.com'
						}
					]
				}
			}
		},
		'URL.PASSWORD': {
			default: {
				category: 'HTTP',
				description: 'Gets the password portion of a URL.',
				inlineDescription: 'Gets the password portion of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Password within URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.PASSWORD("https://admin:s3cr3t@cedalo.com")',
							result: 's3cr3t'
						}
					]
				}
			}
		},
		'URL.PATHNAME': {
			default: {
				category: 'HTTP',
				description: 'Gets the path portion of a URL.',
				inlineDescription: 'Gets the path portion of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Path of a URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.PATHNAME("https://cedalo.com/blog/")',
							result: '/blog/'
						}
					]
				}
			}
		},

		'URL.PORT': {
			default: {
				category: 'HTTP',
				description: 'Gets the port of a URL.',
				inlineDescription: 'Gets the port of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Port within URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.PORT("mqtt://test.mosquitto.org:1883")',
							result: '1883'
						}
					]
				}
			}
		},
		'URL.PROTOCOL': {
			default: {
				category: 'HTTP',
				description: 'Gets the protocol of a URL.',
				inlineDescription: 'Gets the protocol of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Protocol of a URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.PROTOCOL("mqtt://test.mosquitto.org:1883")',
							result: 'mqtt:'
						}
					]
				}
			}
		},
		'URL.QUERY': {
			default: {
				category: 'HTTP',
				description: 'Gets the query string  of a URL.',
				inlineDescription: 'Gets the query string  of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Query string of a URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.QUERY("https://cedalo.com/blog?sort=date")',
							result: '?sort=date'
						}
					]
				}
			}
		},
		'URL.USERNAME': {
			default: {
				category: 'HTTP',
				description: 'Gets the username portion of a URL.',
				inlineDescription: 'Gets the username portion of a URL.',
				arguments: [
					{
						type: 'String',
						name: 'URL',
						description: 'The URL.',
						optional: false
					}
				],
				return: {
					type: 'String',
					description: 'Username of a URL'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=URL.USERNAME("https://admin:s3cr3t@cedalo.com")',
							result: 'admin'
						}
					]
				}
			}
		}
	}
};
